import { useEffect, useRef, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { addMeetingDevices, getAvailableMeetingDevices, type AvailableMeetingDevice } from '../api/api-setting-meeting';
import type { ApiError } from '@/api/api-axios';

export function AddMeetingDevicesDialog({meetingId, onAdded}: {meetingId: string; onAdded: () => Promise<void>}) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<AvailableMeetingDevice[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setDevices([]); setSelected([]); setSearch(''); setLoading(true); setLoadError(false);
    getAvailableMeetingDevices(meetingId).then(response => {
      if (active) setDevices(response.data);
    }).catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, meetingId, retry]);

  const invite = async () => {
    if (!selected.length || busy.current) return;
    busy.current = true; setSaving(true);
    try {
      await addMeetingDevices(meetingId, selected);
      toast.success('Đã thêm thiết bị và gửi lời mời vào họp');
      setOpen(false);
    } catch (error) {
      toast.error((error as ApiError)?.data?.error?.message || 'Không gửi được lời mời. Vui lòng thử lại.');
    } finally {
      // Refresh even on MQTT failure: the backend may already have saved the selection.
      await onAdded().catch(() => {});
      busy.current = false; setSaving(false);
    }
  };

  const query = search.trim().toLocaleLowerCase('vi');
  const filtered = devices.filter(d => `${d.deviceName} ${d.imei}`.toLocaleLowerCase('vi').includes(query));
  return <Dialog open={open} onOpenChange={value => { if (!busy.current) setOpen(value); }}>
    <Button type="button" variant="outline" onClick={() => setOpen(true)}>
      <PlusIcon className="size-4 mr-2"/>Thêm thiết bị vào họp
    </Button>
    <DialogContent className="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>Thêm thiết bị vào họp</DialogTitle>
        <DialogDescription>Chọn thiết bị để gửi lời mời. Có thể thêm lại thiết bị đã được cho rời cuộc họp.</DialogDescription>
      </DialogHeader>
      <Input aria-label="Tìm thiết bị" placeholder="Tìm theo tên hoặc IMEI…" value={search} onChange={e => setSearch(e.target.value)} disabled={saving}/>
      <div className="max-h-72 overflow-y-auto space-y-2">
        {loading && <p role="status">Đang tải thiết bị…</p>}
        {loadError && <div role="alert">Không tải được danh sách thiết bị. <Button variant="outline" onClick={() => setRetry(n => n + 1)}>Thử lại</Button></div>}
        {!loading && !loadError && !filtered.length && <p className="text-sm text-muted-foreground">Không có thiết bị phù hợp để thêm.</p>}
        {filtered.map(device => <label key={device.id} className="flex cursor-pointer items-center gap-3 rounded border p-3">
          <Checkbox aria-label={`Chọn ${device.deviceName}`} checked={selected.includes(device.id)} disabled={saving}
            onCheckedChange={checked => setSelected(ids => checked === true ? [...ids, device.id] : ids.filter(id => id !== device.id))}/>
          <span className="min-w-0 flex-1"><span className="block truncate font-medium">{device.deviceName}</span>
            <span className="block text-xs text-muted-foreground">{device.imei} · {device.connectionStatus === 'connected' ? 'Trực tuyến' : 'Ngoại tuyến'}{device.isInMeeting ? ' · Đang trong cuộc họp' : ''}</span>
          </span>
        </label>)}
      </div>
      <p className="text-sm text-muted-foreground">Đã chọn {selected.length} thiết bị. Thiết bị ngoại tuyến sẽ nhận lời mời khi kết nối lại trong thời gian cuộc họp.</p>
      <DialogFooter>
        <Button variant="outline" disabled={saving} onClick={() => setOpen(false)}>Hủy</Button>
        <Button disabled={loading || loadError || saving || !selected.length} onClick={invite}>{saving ? 'Đang gửi…' : 'Thêm và mời'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
